import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, PlugZap, ShieldCheck, ServerCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader } from "@/components/AdminLayout";
import { PendingBadge } from "@/components/StatusBadge";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AddDialog = ({ open, onOpenChange, catalog, onCreated }) => {
  const [v, setV] = useState({ name: "", provider_key: "", mode: "test", enabled: true, currency: "USD" });
  const submit = async () => {
    if (!v.name.trim() || !v.provider_key) { toast.error("Indica nombre y proveedor"); return; }
    try { await api.post("/processors", v); toast.success("Procesador registrado"); onCreated(); onOpenChange(false); setV({ name: "", provider_key: "", mode: "test", enabled: true, currency: "USD" }); }
    catch (e) { toast.error(apiError(e).message); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-processor-dialog">
        <DialogHeader><DialogTitle>Registrar procesador</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Proveedor</Label>
            <Select value={v.provider_key || undefined} onValueChange={(k) => setV((s) => ({ ...s, provider_key: k, name: s.name || catalog.find((c) => c.key === k)?.label || "" }))}>
              <SelectTrigger data-testid="processor-provider-select"><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>{catalog.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Nombre visible</Label><Input data-testid="processor-name-input" value={v.name} onChange={(e) => setV((s) => ({ ...s, name: e.target.value }))} /></div>
          <div className="space-y-1.5"><Label>Moneda que cobra</Label>
            <Select value={v.currency} onValueChange={(cur) => setV((s) => ({ ...s, currency: cur }))}>
              <SelectTrigger data-testid="processor-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="USD">USD · Dólares</SelectItem><SelectItem value="MXN">MXN · Pesos mexicanos</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Modo</Label>
            <Select value={v.mode} onValueChange={(m) => setV((s) => ({ ...s, mode: m }))}>
              <SelectTrigger data-testid="processor-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="test">Prueba</SelectItem><SelectItem value="live">Producción</SelectItem></SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-3 text-sm"><Switch checked={v.enabled} onCheckedChange={(e) => setV((s) => ({ ...s, enabled: e }))} data-testid="processor-enabled-switch" /> Habilitado</label>
          <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">Registrar un procesador no lo conecta. La conexión requiere un adaptador en el servidor y credenciales en variables de entorno.</p>
        </div>
        <DialogFooter><Button data-testid="processor-save-btn" onClick={submit} className="bg-[#2F5BFF] hover:bg-[#1F45E0]">Registrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ProcessorCard = ({ p, catalog, onPatch, onDelete, readOnly }) => {
  const meta = catalog.find((c) => c.key === p.provider_key);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`processor-card-${p.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-heading text-xl font-semibold">{p.name}</p>
          <p className="text-sm text-slate-500">{meta?.label} · <span className="font-semibold text-slate-700" data-testid={`processor-currency-${p.id}`}>{p.currency}</span></p>
        </div>
        {p.connected
          ? <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700" data-testid={`processor-connected-${p.id}`}>Conectado</span>
          : <PendingBadge testid={`processor-pending-${p.id}`} />}
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-5">
        <label className="flex items-center gap-2 text-sm"><Switch disabled={readOnly} checked={p.enabled} onCheckedChange={(e) => onPatch(p.id, { enabled: e })} data-testid={`processor-toggle-${p.id}`} />{p.enabled ? "Habilitado" : "Deshabilitado"}</label>
        <Select disabled={readOnly} value={p.mode} onValueChange={(m) => onPatch(p.id, { mode: m })}>
          <SelectTrigger className="w-40" data-testid={`processor-mode-${p.id}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="test">Modo prueba</SelectItem><SelectItem value="live">Producción</SelectItem></SelectContent>
        </Select>
        {!readOnly && <Button variant="ghost" size="sm" className="ml-auto text-rose-600" onClick={() => onDelete(p)} data-testid={`processor-delete-${p.id}`}><Trash2 size={15} className="mr-1" />Eliminar</Button>}
      </div>
      {!readOnly && !p.connected && meta?.required_env?.[p.mode]?.length > 0 && (
        <p className="mt-4 rounded-lg bg-amber-50 p-3 text-xs leading-relaxed text-amber-900" data-testid={`processor-required-env-${p.id}`}>
          <span className="font-semibold">Para conectar, define en Secrets: </span>{meta.required_env[p.mode].join(", ")}
        </p>
      )}
      {!readOnly && meta?.has_adapter && meta?.webhook_path && (
        <p className="mt-3 break-all text-xs text-slate-500" data-testid={`processor-webhook-${p.id}`}>
          <span className="font-semibold text-slate-700">Webhook: </span>{`${window.location.origin}${meta.webhook_path.replace("{mode}", p.mode)}`}
        </p>
      )}
      {meta?.checkout_note && <p className="mt-4 border-t border-slate-100 pt-4 text-xs leading-relaxed text-slate-500"><span className="font-semibold text-slate-700">Personalización del checkout: </span>{meta.checkout_note}</p>}
    </div>
  );
};

const Guide = () => (
  <div className="mb-8 grid gap-4 md:grid-cols-3">
    {[[ServerCog, "Adaptador por proveedor", "Cada proveedor se implementa como un adaptador en backend/processors con create_checkout y verificación de webhooks."],
      [ShieldCheck, "Credenciales en el servidor", "Las claves secretas se definen solo como variables de entorno del backend. Nunca en el navegador, el repositorio ni los enlaces."],
      [PlugZap, "Conexión", "Un procesador aparece como Conectado únicamente cuando su adaptador existe y sus credenciales del modo elegido están configuradas."]]
      .map(([Icon, t, d]) => (
        <div key={t} className="rounded-2xl border border-slate-200 bg-white p-5">
          <Icon size={20} className="text-[#2F5BFF]" />
          <p className="mt-3 font-semibold">{t}</p>
          <p className="mt-1 text-sm text-slate-500">{d}</p>
        </div>
      ))}
  </div>
);

export default function Processors() {
  const [list, setList] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [adding, setAdding] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const { user } = useAuth();
  const readOnly = user?.role !== "admin";

  const load = () => api.get("/processors").then((r) => setList(r.data));
  useEffect(() => { load(); api.get("/processors/catalog").then((r) => setCatalog(r.data)); }, []);

  const patch = async (id, body) => {
    try { const { data } = await api.patch(`/processors/${id}`, body); setList((l) => l.map((p) => (p.id === id ? data : p))); }
    catch (e) { toast.error(apiError(e).message); }
  };
  const del = async () => {
    try { await api.delete(`/processors/${toDelete.id}`); toast.success("Procesador eliminado"); load(); }
    catch (e) { toast.error(apiError(e).message); }
  };

  return (
    <>
      <PageHeader title="Procesadores de pago" subtitle="Registra proveedores, habilítalos y elige modo de prueba o producción."
        actions={!readOnly && <Button data-testid="btn-add-processor" onClick={() => setAdding(true)} className="bg-[#2F5BFF] hover:bg-[#1F45E0]"><Plus size={16} className="mr-2" />Registrar procesador</Button>} />
      {readOnly ? <p className="mb-6 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500" data-testid="processors-readonly-note">Solo lectura · el administrador gestiona los procesadores.</p> : <Guide />}
      {list && list.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center" data-testid="processors-empty-state">
          <p className="font-heading text-lg font-semibold">Aún no hay procesadores registrados</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">Puedes registrar los proveedores que planeas usar. Mientras no estén conectados podrás guardar borradores y revisar plantillas, pero no generar enlaces reales.</p>
        </div>
      )}
      <div className="grid gap-5 lg:grid-cols-2">
        {(list || []).map((p) => <ProcessorCard key={p.id} p={p} catalog={catalog} onPatch={patch} onDelete={setToDelete} readOnly={readOnly} />)}
      </div>
      <AddDialog open={adding} onOpenChange={setAdding} catalog={catalog} onCreated={load} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }} title="¿Eliminar procesador?" confirmLabel="Eliminar"
        description="Los cobros existentes conservan el nombre del procesador. No se puede eliminar si tiene enlaces activos." onConfirm={del} />
    </>
  );
}
